import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { functionsService } from '../services/api';
import { getAxiosErrorMessage } from '../utils/errorUtils';
import { ArrowLeft, Save, Code } from 'lucide-react';
import toast from 'react-hot-toast';
import Editor from '@monaco-editor/react';

const CreateFunction: React.FC = () => {
  const [name, setName] = useState('');
  const [code, setCode] = useState(`import { serve } from "https://deno.land/std@0.168.0/http/server.ts";

const handler = async (req: Request): Promise<Response> => {
  try {
    const { method, url } = req;
    
    // Beispiel: Environment Variable aus Deno verwenden
    const testVariable = Deno.env.get('test') || 'Variable nicht gesetzt';
    
    if (method === 'GET') {
      return new Response(JSON.stringify({ 
        message: 'Hello from Deno function!',
        timestamp: new Date().toISOString(),
        environment: {
          test: testVariable,
          runtime: 'Deno ' + Deno.version.deno
        }
      }), {
        headers: { 
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*'
        }
      });
    }
    
    if (method === 'POST') {
      const body = await req.json();
      return new Response(JSON.stringify({ 
        received: body,
        timestamp: new Date().toISOString(),
        environment: {
          test: testVariable
        }
      }), {
        headers: { 
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*'
        }
      });
    }
    
    return new Response('Method not allowed', { status: 405 });
  } catch (_error) {
    return new Response(JSON.stringify({ 
      error: error.message,
      timestamp: new Date().toISOString()
    }), { 
      status: 500,
      headers: { 
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
      }
    });
  }
};

export default handler;`);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Bitte geben Sie einen Namen für die Function ein');
      return;
    }

    if (!code.trim()) {
      toast.error('Bitte geben Sie Code für die Function ein');
      return;
    }

    setLoading(true);

    try {
      await functionsService.create(name.trim(), code);
      toast.success('Function erfolgreich erstellt!');
      navigate('/');
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Erstellen der Function'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Action Bar - Always Visible */}
      <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: 'white', padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/')}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: '#6b7280', 
                fontSize: '14px',
                marginRight: '16px',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück
            </button>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              backgroundColor: '#eff6ff', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <Code className="h-4 w-4" style={{ color: '#2563eb' }} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', margin: 0 }}>
              Neue Function erstellen
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, backgroundColor: '#f9fafb', padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '32px' }}>
            {/* Function Name */}
            <div style={{ marginBottom: '32px' }}>
              <label htmlFor="name" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Function Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                placeholder="z.B. hello-world"
                required
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                Der Name kann nur Buchstaben, Zahlen, Bindestriche und Unterstriche enthalten.
              </p>
            </div>

            {/* Code Editor */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Function Code
              </label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                <Editor
                  height="500px"
                  defaultLanguage="typescript"
                  value={code}
                  onChange={(value) => setCode(value || '')}
                  theme="vs-light"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: 'on',
                    roundedSelection: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    wordWrap: 'on',
                    padding: { top: 16, bottom: 16 },
                  }}
                />
              </div>
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                Schreiben Sie Ihre Function-Logik in TypeScript/JavaScript. Die Variable <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>payload</code> enthält die eingehenden Daten.
              </p>
            </div>

            {/* Submit Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 24px',
                  backgroundColor: loading ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  transition: 'background-color 0.2s'
                }}
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Erstelle...' : 'Function erstellen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFunction;
